import { useTranslation } from 'react-i18next'

import BoldSpan from '../../../components/display/BoldSpan'
import { FAQItem } from '../../../components/landing/FAQ'
import { Steps } from '../../../components/landing/IllustratedSteps'
import { ComponentCTALink } from '../../../components/landing/display'

import CreateAListingIcon from './iconography/CreateAListing'
import HostYourStorytellerIcon from './iconography/HostYourStoryteller'
import PublishAndPromoteIcon from './iconography/PublishAndPromote'
import SubmitStoryOffer from './iconography/SubmitStoryOffer'

export const headerImage = {
  medium: {
    width: 620,
    url:
      'https://assets.riparide.com/landing_pages/stories/host/header/medium-fc2ffe7215f59b5dc622038cf8e9c8b3.jpg',
  },
  large: {
    width: 1200,
    url:
      'https://assets.riparide.com/landing_pages/stories/host/header/large-4704df66aedc72fb9bcd74cfd77164e7.jpg',
  },
  hero: {
    width: 1300,
    url:
      'https://assets.riparide.com/landing_pages/stories/host/header/original-3845306ceb8a6a2c98c56b6509ca7a10.jpg',
  },
}

export const headerText = {
  heading: 'Every place has a story',
  subheading:
    'Bring your property and nearby adventures to life with a Riparide Story.',
}

export const faqItems: FAQItem[] = [
  {
    title: 'What’s in it for me?',
    content:
      'You’ll get beautiful images, which are then used to promote your listing and the adventures nearby.  You’ll also get extra exposure on our website and a higher search ranking, plus an additional pathway for people to book. Customers who book through the Story will get a complete itinerary for their adventure based on the experience our storyteller had.',
  },
  {
    title: 'How do you choose which listings to feature in Stories?',
    content:
      'There’s a lot we consider when selecting listings to feature in stories. Seasonal travel trends in regions is one aspect, eg we might look to cover coastal regions coming into summer and high country regions for winter. We also take into consideration how a property would look in photos based on its styling and features.',
  },
  {
    title: 'How do you match Storytellers and listings?',
    content:
      'Firstly we understand the travel desires of the storyteller and their group, then we look at dates and availability of listings, then we find the most appropriate listing match, ie a more adventurous group of explorers would be matched with a rustic cabin, whilst a group of city slickers might be matched with something with a little more lux and with an architecturally-designed style.',
  },
  {
    title: 'Do I get a copy of the photos?',
    content:
      "If you've offered a complimentary or very heavily discounted stay, you’ll receive a minimum of 10 photos (3MB) of your listing. The photos package will contain external, internal and key feature shots.",
  },
  {
    title:
      'Can I use the images on my website / Instagram / other marketing channel?',
    content:
      'The photos can be used on your own website and social media channels, however they cannot be used across other booking platforms, i.e. Airbnb, Booking.com etc, or third party promotional websites, i.e. Broadsheet, Time Out etc. If you’re posting the images on social media, you will have to credit Riparide & the storyteller by tagging both in the post’s caption.',
  },
]

const CreateAListingExplanation = () => {
  const [t] = useTranslation('spelling')

  return (
    <>
      Before we can {t('organize')} your Riparide Story, you’ll need to list
      your property and become an official Riparide host. Welcome to the club.
    </>
  )
}

export const howItWorksSteps: Steps = [
  {
    title: 'Create a listing',
    explanation: <CreateAListingExplanation />,
    IllustrationComponent: CreateAListingIcon,
  },
  {
    title: 'Submit your story offer',
    explanation:
      'This is what you’re able to provide in exchange for the shoot – generally two nights free. We work with photographers who usually charge $1k to $3k per day, so you’re getting an epic deal.',
    IllustrationComponent: SubmitStoryOffer,
  },
  {
    title: 'Host your storyteller',
    explanation:
      'Your storyteller and travel crew will come to stay, explore and document their experience at your property. Photos will be taken, adventures will be had.',
    IllustrationComponent: HostYourStorytellerIcon,
  },
  {
    title: 'Publish and promote',
    explanation:
      'Your story is published, your photos are shared and your property is now a bookable experience. Go forth, get bookings and inspire soul-fulfilling escapes!',
    IllustrationComponent: PublishAndPromoteIcon,
  },
]

export const benefitsImages = [
  {
    medium:
      'https://assets.riparide.com/landing_pages/stories/host/benefits/1/medium-8decf901476e5544354256041af6cc20.jpg',
    large:
      'https://assets.riparide.com/landing_pages/stories/host/benefits/1/large-41210fba13842d95a6255ecbfa281a4e.jpg',
  },
  {
    medium:
      'https://assets.riparide.com/landing_pages/stories/host/benefits/2/medium-f1cc7e11edd56a73584be7a36b0dace8.jpg',
    large:
      'https://assets.riparide.com/landing_pages/stories/host/benefits/2/large-da0ae88dc0f14250ce330e7f5c907336.jpg',
  },
  {
    medium:
      'https://assets.riparide.com/landing_pages/stories/host/benefits/3/medium-a72f9390d00142c3b6978f3e847ef93d.jpg',
    large:
      'https://assets.riparide.com/landing_pages/stories/host/benefits/3/large-a19a19386d7834df6bffe62eb1f81afc.jpg',
  },
  {
    medium:
      'https://assets.riparide.com/landing_pages/stories/host/benefits/4/medium-29466b88dec5e990a62bc0f9a6ed35bc.jpg',
    large:
      'https://assets.riparide.com/landing_pages/stories/host/benefits/4/large-748ddbfdb5873764bd72986b78f087d3.jpg',
  },
  {
    medium:
      'https://assets.riparide.com/landing_pages/stories/host/benefits/5/medium-0d1fa2351c65fefbfca87f3a2c58e6f7.jpg',
    large:
      'https://assets.riparide.com/landing_pages/stories/host/benefits/5/large-8635734608d3b812eb292b87d95f4e20.jpg',
  },
  {
    medium:
      'https://assets.riparide.com/landing_pages/stories/host/benefits/6/medium-705a8d263bb3ace909e97a49ffc2cd68.jpg',
    large:
      'https://assets.riparide.com/landing_pages/stories/host/benefits/6/large-8c25b1e9a898b1057c31755040468887.jpg',
  },
  {
    medium:
      'https://assets.riparide.com/landing_pages/stories/host/benefits/7/medium-d37349efaf7f02e50a2ed2b8227b198a.jpg',
    large:
      'https://assets.riparide.com/landing_pages/stories/host/benefits/7/large-ceaab922dd8c9fe86104dd9506085ba7.jpg',
  },
  {
    medium:
      'https://assets.riparide.com/landing_pages/stories/host/benefits/8/medium-2878cdcfe653663a230afffa3d23556e.jpg',
    large:
      'https://assets.riparide.com/landing_pages/stories/host/benefits/8/large-4535eb31ca36c74ab003fde02729a2a4.jpg',
  },
]

export const briefText = (
  <>
    <BoldSpan>Riparide Stories</BoldSpan> are real travel experiences captured
    and shared by our creative community of photographers, who we call
    Storytellers. Through inspiring photos and words, our Storytellers bring
    your property and nearby adventures to life.
    <br />
    <br />
    Stories not only drive more bookings but they also inspire people to get out
    into nature and live fully.
    <br />
    <br />
    Join us on our mission to deliver soul-fulfilling escapes.
  </>
)

const MoreExposureText = () => {
  const [t] = useTranslation('spelling')

  return (
    <>
      Properties with Riparide Stories actually get higher search results on
      Riparide.com. We also feature our {t('favorite')} Stories across our
      social media channels for more exposure.
    </>
  )
}

export const benefitsText = [
  {
    heading: 'Match with a storyteller',
    text:
      'We’ll match you with the perfect storyteller and their travel crew to bring your property to life. We only work with talented photographers who share our passion for exploring.',
  },
  {
    heading: 'Drive more bookings',
    text:
      'We’ve found that listings with a Riparide Story earn, on average, 46% more bookings than listings without a Story. And most of them are couples looking to connect with nature and explore the outdoors!',
  },
  {
    heading: 'Get beautiful photos',
    text:
      'After the Storyteller shoot, we’ll send you a collection of beautiful shots. We only deal in photography that truly captures the vibe of your property.',
  },
  {
    heading: 'More exposure',
    text: <MoreExposureText />,
  },
  {
    heading: 'Make a difference',
    text:
      'Your Riparide Story will inspire people to live their best, most fulfilling lives. Guest fulfillment is our top priority – we even measure it at the end of every stay.',
  },
]

export const testimonials = [
  {
    name: 'Carl Ward',
    affiliation: {
      text: 'Japanese Zen Retreat',
    },
    avatar: {
      thumb:
        'https://assets.riparide.com/user/1525/avatar/thumb-c9c221a0a0780d723e2c3bee273dced9.jpg',
      thumbMd:
        'https://assets.riparide.com/user/1525/avatar/thumb_md-a8032472e0871ada968f44098e71d042.jpg',
      thumbLg:
        'https://assets.riparide.com/user/1525/avatar/thumb_lg-d533fbfbe372166b06d6228a59b58ab8.jpg',
      original:
        'https://assets.riparide.com/user/1525/avatar/8fab6209f6be1e6475f18a0d971707e8.jpg',
    },
    text:
      'Since using the Riparide platform, my B&B has enjoyed a 30% increase in business. The clientele has been lovely and the story on the Japanese Zen Retreat has boosted reservations, which has been brilliant for my business and revenue.',
    CTA: (
      <ComponentCTALink url="/stories/116-now-and-zen">
        View Story
      </ComponentCTALink>
    ),
  },
  {
    name: 'Lisa Deppeler',
    affiliation: {
      text: 'Aire River Escape',
    },
    avatar: {
      thumb: 'https://assets.riparide.com/avatars/2626/thumb.jpg',
      thumbMd: 'https://assets.riparide.com/avatars/2626/thumb_md.jpg',
      thumbLg: 'https://assets.riparide.com/avatars/2626/thumb_lg.jpg',
      original: 'https://assets.riparide.com/avatars/2626/original.jpg',
    },
    text:
      'Riparide sent Jesse to my property to provide photos and a story about his weekend at my property. This form of marketing proved to be very popular and Riparide is now my major booking platform.',
    CTA: (
      <ComponentCTALink url="/stories/91-the-aire-down-there">
        View Story
      </ComponentCTALink>
    ),
  },
  {
    name: 'Melanie Brown',
    affiliation: {
      text: 'Vintage 1967 Franklin Caravelle Van',
    },
    avatar: {
      thumb:
        'https://assets.riparide.com/user/12521/avatar/thumb-7c3e3e467c47f158d4a186a2c641a7e2.jpg',
      thumbMd:
        'https://assets.riparide.com/user/12521/avatar/thumb_md-0cc5676b76eb137e84ab532a13f5714c.jpg',
      thumbLg:
        'https://assets.riparide.com/user/12521/avatar/thumb_lg-5366030cce7040ab5030af4474a6e0aa.jpg',
      original:
        'https://assets.riparide.com/user/12521/avatar/original-da216d630fb241c2bf217246b3072a51.jpg',
    },
    text:
      'Participating in a Riparide Story was a great experience that gave us additional exposure and led to bookings we could never have achieved on our own. We feel that being a part of Riparide is being part of a genuine, supportive team working together to offer our guests a unique and special stay.',
    CTA: (
      <ComponentCTALink url="/stories/222-disconnect-to-reconnect">
        View Story
      </ComponentCTALink>
    ),
  },
  {
    name: 'Fay Bushby',
    affiliation: {
      text: 'The Barn at Heathcote',
    },
    avatar: {
      thumb: 'https://assets.riparide.com/avatars/2163/thumb.jpg',
      thumbMd: 'https://assets.riparide.com/avatars/2163/thumb_md.jpg',
      thumbLg: 'https://assets.riparide.com/avatars/2163/thumb_lg.jpg',
      original: 'https://assets.riparide.com/avatars/2163/original.jpg',
    },
    text:
      "I love that Riparide is promoting all the wonderful things to do in the area through their stories. I’ve had Bushies Love Shack featured in two stories and I really think they’ve helped to enhance my guests' experience by encouraging them to get out and explore our wonderful region.",
    CTA: (
      <ComponentCTALink url="/stories/142-autumn-leaves">
        View Story
      </ComponentCTALink>
    ),
  },
  {
    name: 'Jacob Stammers',
    affiliation: {
      text: 'Bushies Love Shack',
    },
    avatar: {
      thumb: 'https://assets.riparide.com/avatars/3861/thumb.jpg',
      thumbMd: 'https://assets.riparide.com/avatars/3861/thumb_md.jpg',
      thumbLg: 'https://assets.riparide.com/avatars/3861/thumb_lg.jpg',
      original: 'https://assets.riparide.com/avatars/3861/original.jpg',
    },
    text:
      'Riparide guests seem to be chilled humans who understand the quirks of our property and really embrace the experience! We are also stoked with the ongoing support we’ve received – social media posts and the Riparide Story – which have meant heaps of bookings and really helps us stand out and get people excited for their adventure!',
    CTA: (
      <ComponentCTALink url="/stories/92-barn-country">
        View Story
      </ComponentCTALink>
    ),
  },
]

export const storiesForCarousel = [
  {
    id: '556',
    url: '/stories/556-surf-n-turf',
    title: "Surf 'n' Turf ",
    promoImage: {
      large:
        'https://assets.riparide.com/story/556/promo_image/large-03733d3d4586759820901e948ef2705d.jpg',
    },
    user: {
      id: '5255',
      firstName: 'Chloe',
      avatar: {
        thumbLg:
          'https://assets.riparide.com/user/5255/avatar/thumb_lg-68c0bdba74744de5e2c6f90bdecbe89d.jpg',
      },
    },
  },
  {
    id: '591',
    url: '/stories/591-gapsted-hidden-gem',
    title: 'Gapsted Hidden Gem',
    promoImage: {
      large:
        'https://assets.riparide.com/story/591/promo_image/large-fb1444b7328e6b5d4772ddd72de2322f.jpg',
    },
    user: {
      id: '267',
      firstName: 'Andrew & Jessie',
      avatar: {
        thumbLg:
          'https://assets.riparide.com/user/267/avatar/thumb_lg-e6ca6266826f4a5d7ad594479e28b76f.jpg',
      },
    },
  },
  {
    id: '593',
    url: '/stories/593-indulgence-at-the-longhouse',
    title: 'Indulgence at the Longhouse',
    promoImage: {
      large:
        'https://assets.riparide.com/story/593/promo_image/large-fec2780b8748a5a71c10dcbae1f19161.jpg',
    },
    user: {
      id: '8691',
      firstName: 'Trent ',
      avatar: {
        thumbLg:
          'https://assets.riparide.com/user/8691/avatar/thumb_lg-9a0f8dfb525d09968325bfe9c105a945.jpg',
      },
    },
  },
  {
    id: '414',
    url: '/stories/414-feels-like-home',
    title: 'Feels like home',
    promoImage: {
      large:
        'https://assets.riparide.com/story/414/promo_image/large-07ff92f1032fabaa8c3da1c21d5c3924.jpg',
    },
    user: {
      id: '4051',
      firstName: 'Arianna',
      avatar: {
        thumbLg:
          'https://assets.riparide.com/user/4051/avatar/thumb_lg-b25f2df069469ec9c8e544bcdcd850fd.jpg',
      },
    },
  },
  {
    id: '332',
    url: '/stories/332-a-place-for-the-whole-pack',
    title: 'A Place for the Whole Pack',
    promoImage: {
      large:
        'https://assets.riparide.com/story/332/promo_image/large-84d65cf9e2186733534f01d18cd91159.jpg',
    },
    user: {
      id: '8678',
      firstName: 'Jaccob & Sunshine',
      avatar: {
        thumbLg:
          'https://assets.riparide.com/user/8678/avatar/thumb_lg-59fb1529b9bf935632d5d873acb00816.png',
      },
    },
  },
  {
    id: '70',
    url: '/stories/70-stars-a-billion',
    title: 'Stars a Billion',
    promoImage: {
      large:
        'https://assets.riparide.com/story/70/promo_image/large-9428daf429bf05f0215b4f6a4e5fe217.jpg',
    },
    user: {
      id: '5693',
      firstName: 'Connor',
      avatar: {
        thumbLg: 'https://assets.riparide.com/avatars/5693/thumb_lg.png',
      },
    },
  },
  {
    id: '650',
    url: '/stories/650-wild-wild-nest',
    title: 'Wild Wild Nest',
    promoImage: {
      large:
        'https://assets.riparide.com/story/650/promo_image/large-83fe7b37c6a58e902fae211378df680f.jpg',
    },
    user: {
      id: '7856',
      firstName: 'Alexandra',
      avatar: {
        thumbLg:
          'https://assets.riparide.com/user/7856/avatar/thumb_lg-8f75cd65126d20a7aa7e8ef9ac87ebd8.jpg',
      },
    },
  },
  {
    id: '605',
    url: '/stories/605-quiet-paradise',
    title: 'Quiet Paradise',
    promoImage: {
      large:
        'https://assets.riparide.com/story/605/promo_image/large-68d108c6c459edb9434b8986176bfe35.jpg',
    },
    user: {
      id: '7856',
      firstName: 'Alexandra',
      avatar: {
        thumbLg:
          'https://assets.riparide.com/user/7856/avatar/thumb_lg-8f75cd65126d20a7aa7e8ef9ac87ebd8.jpg',
      },
    },
  },
]
